import { AppCollapsible, Rank } from "@components"
import type { Database } from "@hvf/contracts"
import { For } from "solid-js"
import * as Card from "src/components/ui/card"
import { Heading } from "src/components/ui/heading"
import { css } from "styled-system/css"
import { Flex } from "styled-system/jsx"

interface RankConfig {
  title: string
  boldText: string
  description: string
  requirements?: string[]
  benefits?: string[]
}

const RanksConfig: Record<Database['public']['Enums']['role'], RankConfig> = {
  admin: {
    title: 'Admin',
    boldText: 'Platform Administrators',
    description: 'Admins are responsible for the management, moderation, and technical operation of The Sniper Circle platform.'
  },
  commander: {
    title: 'Commander in Chief',
    boldText: 'The commander of the HVF Community',
    description: 'With the highest level of competency and experience in trading HVF Method – there can only be one Commander in Chief.'
  },
  captain: {
    title: 'Captain',
    boldText: 'Leaders within The Sniper Circle community',
    description: 'Captains are leaders within The Sniper Circle Community. They are competent HVF Traders and fulfil many supportive tasks within the community.'
  },
  full_lieutenant: {
    title: 'Full Lieutenant',
    boldText: 'Highly competent HVF Traders and helpful within The Sniper Circle community and tasked with presenting webinars on a regular basis.',
    description: 'A Full Lieutenant is highly trained HVF Traders who deliver a high degree of service to The Sniper Circle including regular webinar presentations.',
    requirements: [
      'Being fully committed toward HVF Method trading education through the completion of various advanced training modules.',
      'Demonstrating a high degree of understanding of HVF Method trading through posts of setups and discussions on TheSniperCircle.com',
      'Availability to present HVF webinars when called upon.'
    ],
    benefits: [
      'Recognition on TheSniperCircle.com community',
      'Offered 20% discount to subscription renewals',
      'Eligibility for promotion to Captain'
    ]
  },
  second_lieutenant: {
    title: '2nd Lieutenant',
    boldText: 'More competent HVF Traders also sometimes tasked with giving presentations to The Sniper Circle Community.',
    description: 'Second Lieutenants have completed all their training and are likely to have demonstrated an ability to trade advanced HVF techniques independently. As such they are competent HVF traders and generally make valuable contributions in terms of setups and support discussions on the Sniper Circle community.',
    requirements: [
      'Up to date with all courses on the Course Library',
      'Have demonstrated an ability to trade advanced HVF techniques independently.',
      'Posting high-quality setups on TheSniperCircle.com community'
    ],
    benefits: [
      'Recognition on TheSniperCircle.com community',
      'Offered 20% discount to subscription renewals',
      'Eligibility for promotion to Full Lieutenant'
    ]
  },
  staff_sergeant: {
    title: 'Staff Sergeant',
    boldText: 'Competent HVF Traders and supportive community members.',
    description: 'Staff Sergeants are competent HVF Traders and highly supportive toward other members of the community.',
    requirements: [
      'Demonstrating a high level of understanding of HVF Method through posting quality HVF Setups on TheSniperCircle.com',
      'Generally supportive toward other members of The Sniper Circle Community through forum and chat discussions'
    ],
    benefits: [
      'Recognition within TheSniperCircle.com Community',
      'Offered 20% discount on subscription renewal fees',
      'Eligibility for promotion to 2nd Lieutenant'
    ]
  },
  sergeant: {
    title: 'Sergeant',
    boldText: 'Competent HVF Traders',
    description: 'High ranking officer of the Sniper Circle community with a very high understanding of the HVF Method and have shown willingness to support junior members.',
    requirements: [
      'Good quality posting of HVF Setups on TheSniperCircle.com',
      'Supportive and generally helpful to HVF related questions on forums and chat discussion on TheSniperCircle.com'
    ],
    benefits: [
      'Recognition on TheSniperCircle.com community',
      'Offered 20% discount on subscription renewal fees',
      'Eligibility for promotion to Staff Sergeant'
    ]
  },
  corporal: {
    title: 'Corporal',
    boldText: 'Proficient HVF Traders and supportive community members',
    description: 'More proficient HVF traders have shown a high level of understanding of HVF trading through their posting of quality setups and support for junior members in the Sniper Circle community.',
    requirements: [
      'Completed most of the HVF education found in the Courses Library',
      'Regular posting of setups on TheSniperCircle.com',
      'Support and answering HVF Method related questions in forums and chat.'
    ],
    benefits: [
      'Recognition within the Sniper Circle Community',
      'Offered 15% discount on subscription renewal fees',
      'Eligible for promotion to Rank Sergeant'
    ]
  },
  lance_corporal: {
    title: 'Lance Corporal',
    boldText: 'Developing HVF Traders',
    description: 'Lance Corporals are progressing well in their HVF trading journey and actively applying their knowledge.',
    requirements: [
      'Active participation in The Sniper Circle community',
      'Beginning to post basic setups for feedback'
    ],
    benefits: [
      'Recognition within the community',
      'Offered 10% discount on subscription renewal fees',
      'Eligible for promotion to Corporal'
    ]
  },
  rifleman: {
    title: 'Rifleman',
    boldText: 'Beginner HVF Traders',
    description: 'Riflemen are relatively new to the HVF Method and are actively working through the foundational educational content.',
    requirements: [
      'Consistent engagement with educational materials',
      'Active learning in the community forums'
    ],
    benefits: [
      'Access to core community resources',
      'Eligible for promotion to Lance Corporal'
    ]
  },
  recruit: {
    title: 'Recruit',
    boldText: 'New Community Members',
    description: 'Recruits are brand new members who have just joined The Sniper Circle and are taking their first steps.',
    requirements: [
      'Join The Sniper Circle community'
    ],
    benefits: [
      'Access to introductory HVF Method training',
      'Eligible for promotion to Rifleman'
    ]
  }
}

// Defining the strict render order ensures the visual hierarchy matches the chain of command
const rankKeys: Database['public']['Enums']['role'][] = [
  'admin',
  'commander',
  'captain',
  'full_lieutenant',
  'second_lieutenant',
  'staff_sergeant',
  'sergeant',
  'corporal',
  'lance_corporal',
  'rifleman',
  'recruit',
]

export default function RanksPage() {
  return (
    <article class={css({ display: 'grid', gap: 6 })}>

      <div class={css({ display: 'grid', gap: 4 })}>
        <For each={rankKeys}>
          {roleKey => {
            const config = RanksConfig[roleKey]

            return (
              <Card.Root gridTemplateRows="auto 1fr" display="grid">
                <AppCollapsible
                  headerContent={
                    <Flex justifyContent="space-between" alignItems="center" height={12} p={3} gap='3'>
                      <Rank rank={roleKey} />
                      <Heading textStyle="sm">{config.title}</Heading>
                    </Flex>
                  }
                >
                  <div class={css({ p: 4, display: 'grid', gap: 3 })}>
                    <p><strong>{config.boldText}</strong></p>
                    <p>{config.description}</p>

                    {/* Render Requirements only if they exist in the config for this rank */}
                    {config.requirements && config.requirements.length > 0 && (
                      <>
                        <Heading textStyle="md" mt={2}>Requirements:</Heading>
                        <ul class={css({ listStyleType: 'disc', pl: 5, display: 'grid', gap: 1 })}>
                          <For each={config.requirements}>
                            {req => <li>{req}</li>}
                          </For>
                        </ul>
                      </>
                    )}

                    {/* Render Benefits only if they exist in the config for this rank */}
                    {config.benefits && config.benefits.length > 0 && (
                      <>
                        <Heading textStyle="md" mt={2}>Benefits:</Heading>
                        <ul class={css({ listStyleType: 'disc', pl: 5, display: 'grid', gap: 1 })}>
                          <For each={config.benefits}>
                            {benefit => <li>{benefit}</li>}
                          </For>
                        </ul>
                      </>
                    )}
                  </div>
                </AppCollapsible>
              </Card.Root>
            )
          }}
        </For>
      </div>
    </article>
  )
}
